import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { borderBottom, height, minHeight, minWidth, size } from 'styled-system';

import { Button } from 'common/components/Button';
import { Flex } from 'common/components/Flex';
import type { IconName } from 'common/components/Icon';
import Icon from 'common/components/Icon';
import { HeadlineH7 } from 'common/components/Typography';
import {
  borderBottomColor,
  borderBottomStyle,
  borderBottomWidth,
} from 'common/theme/system-utilities';

export interface ReclamationDrawerHeaderProps {
  id: string;
  title?: string;
  buttonLabel: string;
  buttonIcon: IconName;
  onClick: () => void;
}

const ICON_SIZES = ['24px', null, '32px'];

const HEADER_HEIGHT = '56px';

const UnstyledDrawerHeader = styled(Flex)<any>`
  ${borderBottomStyle};
  ${borderBottomWidth};
  ${borderBottomColor};
  ${borderBottom};
  ${height};
  ${minHeight};
`;

const DrawerHeader = (props: ComponentProps<typeof UnstyledDrawerHeader>) => (
  <UnstyledDrawerHeader
    as="header"
    alignItems="center"
    borderBottomStyle="solid"
    borderBottomWidth="small"
    borderBottomColor="sys.neutral.border.weak"
    height={HEADER_HEIGHT}
    minHeight={HEADER_HEIGHT}
    px={['sp_12', 'sp_20', null, 'sp_24']}
    {...props}
  />
);

const UnstyledStyledButton = styled(Button)<any>`
  cursor: pointer;
`;

const StyledButton = (props: ComponentProps<typeof UnstyledStyledButton>) => (
  <UnstyledStyledButton kind="bare" borderRadius="circle" {...props} />
);

const UnstyledStyledIcon = styled(Icon)<any>`
  ${size};
  ${minHeight};
  ${minWidth};
`;

const StyledIcon = (props: ComponentProps<typeof UnstyledStyledIcon>) => (
  <UnstyledStyledIcon size={ICON_SIZES} minHeight={ICON_SIZES} minWidth={ICON_SIZES} {...props} />
);

const UnstyledTitleContainer = styled(Flex)<any>`
  ${height};
`;

const TitleContainer = (props: ComponentProps<typeof UnstyledTitleContainer>) => (
  <UnstyledTitleContainer
    width={1}
    height="100%"
    alignItems="center"
    justifyContent="center"
    {...props}
  />
);

const ReclamationDrawerHeader = ({
  title,
  buttonLabel,
  buttonIcon,
  onClick,
  id,
}: ReclamationDrawerHeaderProps) => (
  <DrawerHeader data-testid="reclamation-drawer-header">
    <StyledButton aria-label={buttonLabel} onClick={onClick}>
      <StyledIcon name={buttonIcon} />
    </StyledButton>
    {!!title && (
      <TitleContainer>
        <HeadlineH7 as="h2" id={id}>
          {title}
        </HeadlineH7>
      </TitleContainer>
    )}
  </DrawerHeader>
);

export default ReclamationDrawerHeader;
